import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Plus, Star } from 'lucide-react';
import { useCart } from '../../lib/cartContext';
import { useRecentlyViewedProducts } from '../../lib/useRecentlyViewedProducts';

function MiniProductCard({ product }) {
  const { addItem, loading } = useCart();
  const [adding, setAdding] = React.useState(false);

  const handleAdd = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setAdding(true);
    await addItem(product.id);
    setAdding(false);
  };

  return (
    <Link
      to={`/shop/${product.slug}`}
      className="flex-shrink-0 w-[200px] group block no-underline"
      data-testid={`recent-product-${product.slug}`}
    >
      <div className="relative aspect-square overflow-hidden bg-zinc-900 mb-3">
        {product.image ? (
          <img src={product.image} alt={product.name} className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-zinc-700 text-xs">No Image</div>
        )}
        <button
          onClick={handleAdd}
          disabled={loading || adding}
          className="absolute bottom-2 right-2 w-8 h-8 bg-[#E63946] text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-[#c52f3b] disabled:opacity-50"
          data-testid={`recent-add-${product.slug}`}
          aria-label="Add to cart"
        >
          {adding ? (
            <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <Plus className="w-4 h-4" />
          )}
        </button>
      </div>
      <p className="text-zinc-500 text-[10px] uppercase tracking-wider">{product.category}</p>
      <h4 className="text-white text-sm mt-0.5 truncate group-hover:text-[#E63946] transition-colors duration-300">
        {product.name}
      </h4>
      <div className="flex items-center gap-2 mt-1">
        <span className="text-[#E63946] text-sm font-semibold">${product.price?.toFixed(2)}</span>
        {product.compare_at_price && product.compare_at_price > product.price && (
          <span className="text-zinc-600 line-through text-xs">${product.compare_at_price.toFixed(2)}</span>
        )}
      </div>
    </Link>
  );
}

export default function RecentlyViewedProducts({ excludeId, title = 'Recently Viewed' }) {
  const { products, getExcluding } = useRecentlyViewedProducts();
  const scrollRef = useRef(null);

  const items = excludeId ? getExcluding(excludeId) : products;

  if (items.length === 0) return null;

  const scroll = (dir) => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({ left: dir * 220, behavior: 'smooth' });
  };

  return (
    <section className="py-12" data-testid="recently-viewed-products">
      <div className="container-ninja">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-[#E63946] text-xs uppercase tracking-wider font-heading mb-1">Keep Shopping</p>
            <h3 className="text-white text-2xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{title}</h3>
          </div>
          <div className="flex gap-2">
            <button onClick={() => scroll(-1)} className="w-9 h-9 flex items-center justify-center border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 transition-colors" aria-label="Scroll left" data-testid="recent-scroll-left">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button onClick={() => scroll(1)} className="w-9 h-9 flex items-center justify-center border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 transition-colors" aria-label="Scroll right" data-testid="recent-scroll-right">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div
          ref={scrollRef}
          className="flex gap-5 overflow-x-auto scrollbar-hide pb-2"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {items.map(product => (
            <MiniProductCard key={product.id} product={product} />
          ))}
        </div>
      </div>
    </section>
  );
}
